import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  FactCheck as GradeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { analyzeEvidence } from '../../api/evidenceApi';
import { loadRemoteProgress, saveRemoteProgress } from '../../api/progressApi';

// Generic engine behind MissingnessPracticeBank / DuplicatePracticeBank /
// OutlierPracticeBank — those three were hand-copied per topic, which is how
// their admin-side lens lists (CohortPilotPage.jsx's DCC_TAKE_HOME_CONFIGS)
// drifted out of sync with the actual lens keys used here. New topics should
// define a config object (see frontend/src/data/practiceBankConfigs/) and
// export its lenses/answerGuide so the admin panel imports the SAME array
// instead of retyping it.
//
// config shape:
// {
//   trackerId: string,               // TrackerProgress id, e.g. 'dq-math-05-invalid-values-practice-bank'
//   resourceKey: string,             // saved for admin/debug context only
//   rubricKey: string,               // semantic-rubric-grade context.rubric_key
//   activityScope: string,           // semantic-rubric-grade context.activity_scope
//   heading: string,
//   description: string,
//   introAlert: string,              // shown above the practice items
//   lenses: [[key, label]],
//   workingField: { key, label, prompts: { [itemId]: string }, placeholder } | null,
//   items: [{
//     id, title, theme,
//     tables: [{ columns, rows }],
//     answerGuide: { [lensKey]: expectedText },
//     ruleTerms: { [lensKey]: string[] },        // rules-fallback keyword match
//     criticalTerms: { [lensKey]: string[] },    // exact scenario values that must appear
//     fallbackMinLength: { [lensKey]: number },  // optional, defaults to 16
//   }],
// }

const normalise = (value) => String(value || '').toLowerCase();
const hasAny = (value, terms = []) => {
  const text = normalise(value);
  return terms.some((term) => text.includes(String(term).toLowerCase()));
};

const lensScore = (value, terms, fallbackMinLength = 16) => {
  const text = normalise(value).trim();
  if (!text) return 0;
  if (hasAny(text, terms)) return 1;
  return text.length >= fallbackMinLength ? 0.5 : 0;
};

const hasCriticalTerm = (value, term) => {
  const text = String(value || '').toLowerCase();
  const token = String(term || '').toLowerCase();
  if (!token) return true;
  return new RegExp(`(^|[^a-z0-9])${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`).test(text);
};

const applyCriticalTermCap = (item, key, value, score) => {
  const criticalTerms = item.criticalTerms?.[key] || [];
  if (!criticalTerms.length || score <= 0) return { score, reason: '' };
  const missingTerms = criticalTerms.filter((term) => !hasCriticalTerm(value, term));
  if (!missingTerms.length) return { score, reason: '' };
  return { score: Math.min(score, 0.5), reason: `Check exact scenario value: ${missingTerms.join(', ')}.` };
};

const emptyResponses = (config) =>
  config.items.reduce((acc, item) => {
    const base = config.workingField ? { [config.workingField.key]: '' } : {};
    acc[item.id] = config.lenses.reduce((lensAcc, [key]) => ({ ...lensAcc, [key]: '' }), base);
    return acc;
  }, {});

const countCompletedItems = (config, responses) =>
  config.items.reduce((count, item) => {
    const row = responses[item.id] || {};
    const workingDone = !config.workingField || String(row[config.workingField.key] || '').trim();
    return count + (workingDone && config.lenses.every(([key]) => String(row[key] || '').trim()) ? 1 : 0);
  }, 0);

const missingRequiredFields = (config, responses) =>
  config.items.map((item) => {
    const row = responses[item.id] || {};
    const missing = [];
    if (config.workingField && !String(row[config.workingField.key] || '').trim()) {
      missing.push(config.workingField.label);
    }
    config.lenses.forEach(([key, label]) => {
      if (!String(row[key] || '').trim()) missing.push(label);
    });
    return { item, missing };
  }).filter(({ missing }) => missing.length);

const formatMissingFields = (missingItems = []) =>
  missingItems.slice(0, 4).map(({ item, missing }) => `${item.title}: ${missing.join(', ')}`).join(' | ');

const gradeItemRules = (config, item, answer = {}) => {
  const checks = config.lenses.map(([key, label]) => {
    const terms = item.ruleTerms?.[key] || [];
    const minLen = item.fallbackMinLength?.[key] || 16;
    const score = lensScore(answer[key], terms, minLen);
    const capped = applyCriticalTermCap(item, key, answer[key], score);
    return [key, label, capped.score, capped.reason];
  });
  const score = checks.reduce((sum, [, , value]) => sum + value, 0);
  const max = checks.length;
  const missing = checks.filter(([, , value]) => value < 1).map(([, label]) => label);
  const strengths = checks.filter(([, , value]) => value >= 1).map(([, label]) => label);
  const lensResults = checks.reduce((acc, [key, label, value, reason]) => ({
    ...acc,
    [key]: {
      label,
      score: value,
      status: value >= 1 ? 'correct' : value > 0 ? 'partial' : 'missed',
      expected: item.answerGuide?.[key] || '',
      reason,
    },
  }), {});
  return {
    score,
    max,
    percent: Math.round((score / max) * 100),
    strengths,
    missing,
    lensResults,
    feedback: missing.length ? `Review: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}.` : 'Strong answer across every lens.',
  };
};

const semanticGradeItem = async (config, item, answer = {}) => {
  const fallback = gradeItemRules(config, item, answer);
  const lenses = config.lenses.map(([key, label]) => ({
    key,
    label,
    user_answer: answer[key] || '',
    expected_answer: item.answerGuide?.[key] || '',
    critical_terms: item.criticalTerms?.[key] || [],
  }));
  try {
    const result = await analyzeEvidence({
      content: [
        `Activity: ${config.heading} semantic grading`,
        `Item: ${item.id}`,
        `Scenario: ${item.scenario || ''}`,
        `Scenario rows: ${JSON.stringify(item.tables)}`,
        config.workingField ? `Learner approach/working: ${answer[config.workingField.key] || ''}` : '',
        `Learner answers: ${JSON.stringify(answer)}`,
        `Expected answers: ${JSON.stringify(item.answerGuide || {})}`,
      ].filter(Boolean).join('\n'),
      analysisType: 'semantic-rubric-grade',
      context: {
        activity_scope: config.activityScope,
        rubric_key: config.rubricKey,
        item_id: item.id,
        scenario: item.scenario,
        lenses,
      },
    });
    if (!result?.lensResults) return { ...fallback, mode: 'rules' };
    return {
      ...fallback,
      ...result,
      score: Number(result.score ?? fallback.score),
      max: Number(result.max ?? fallback.max),
      percent: Number(result.percent ?? fallback.percent),
      strengths: result.strengths || fallback.strengths,
      missing: result.missing || fallback.missing,
      lensResults: result.lensResults,
      feedback: result.feedback || fallback.feedback,
      mode: result.mode || 'semantic',
    };
  } catch (error) {
    return { ...fallback, mode: 'rules', llm_error: error.message || 'Semantic grader unavailable.' };
  }
};

const semanticGradeAllItems = async (config, responses) => {
  const entries = await Promise.all(
    config.items.map(async (item) => [item.id, await semanticGradeItem(config, item, responses[item.id] || {})])
  );
  return entries.reduce((acc, [itemId, grade]) => ({ ...acc, [itemId]: grade }), {});
};

const gradeSummary = (grades = {}) => {
  const entries = Object.values(grades).filter(Boolean);
  if (!entries.length) return null;
  const score = entries.reduce((sum, grade) => sum + grade.score, 0);
  const max = entries.reduce((sum, grade) => sum + grade.max, 0);
  const lensCounts = entries.reduce((acc, grade) => {
    (grade.missing || []).forEach((lens) => { acc[lens] = (acc[lens] || 0) + 1; });
    return acc;
  }, {});
  return {
    score,
    max,
    percent: max ? Math.round((score / max) * 100) : 0,
    weaknesses: Object.entries(lensCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([lens]) => lens),
  };
};

function DataTable({ table }) {
  const theme = useTheme();
  return (
    <Box sx={{ my: 1.5 }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.84rem',
          '& th': { bgcolor: alpha(theme.palette.primary.main, 0.09), fontWeight: 800 },
          '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 1.25, py: 0.85, textAlign: 'left', verticalAlign: 'top' },
        }}>
          <thead><tr>{table.columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
          <tbody>{table.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}</tr>
          ))}</tbody>
        </Box>
      </Box>
    </Box>
  );
}

export default function TakeHomePracticeBank({ config }) {
  const theme = useTheme();
  const [responses, setResponses] = useState(() => emptyResponses(config));
  const [grades, setGrades] = useState({});
  const [loadState, setLoadState] = useState('loading');
  const [saveState, setSaveState] = useState('idle');
  const [gradeState, setGradeState] = useState('idle');
  const [message, setMessage] = useState('');
  const [openSolutions, setOpenSolutions] = useState({});
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  const completedCount = useMemo(() => countCompletedItems(config, responses), [config, responses]);
  const missingItems = useMemo(() => missingRequiredFields(config, responses), [config, responses]);
  const canGrade = loadState !== 'loading' && !missingItems.length;
  const summary = useMemo(() => gradeSummary(grades), [grades]);

  useEffect(() => {
    let cancelled = false;
    loadRemoteProgress(config.trackerId)
      .then((state) => {
        if (cancelled) return;
        setResponses({ ...emptyResponses(config), ...(state?.responses || {}) });
        setGrades(state?.grades || {});
      })
      .catch((error) => {
        if (!cancelled && !String(error?.message || '').includes('Unable to load')) setMessage(error.message || 'Could not load saved answers.');
      })
      .finally(() => {
        if (!cancelled) setLoadState('ready');
        loadedRef.current = true;
      });
    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.trackerId]);

  const saveResponses = async (nextResponses = responses, mode = 'manual', nextGrades = grades) => {
    setSaveState('saving');
    setMessage('');
    try {
      await saveRemoteProgress(config.trackerId, {
        version: 1,
        resource: config.resourceKey,
        responses: nextResponses,
        grades: nextGrades,
        grade_summary: gradeSummary(nextGrades),
        completed_tables: countCompletedItems(config, nextResponses),
        updated_at: new Date().toISOString(),
      });
      setSaveState('saved');
      setMessage(mode === 'auto' ? 'Autosaved.' : 'Saved to your practice record.');
    } catch (error) {
      setSaveState('error');
      setMessage(error.message || 'Could not save your answers.');
    }
  };

  const scheduleSave = (nextResponses, nextGrades = grades) => {
    if (!loadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState('dirty');
    saveTimerRef.current = setTimeout(() => saveResponses(nextResponses, 'auto', nextGrades), 900);
  };

  const updateAnswer = (itemId, key, value) => {
    setResponses((current) => {
      const next = { ...current, [itemId]: { ...(current[itemId] || {}), [key]: value } };
      let nextGrades = grades;
      if (Object.keys(grades).length) {
        nextGrades = { ...grades };
        delete nextGrades[itemId];
        setGrades(nextGrades);
        setMessage(`${itemId} changed. Click Grade answers to run semantic grading again.`);
      }
      scheduleSave(next, nextGrades);
      return next;
    });
  };

  const toggleSolution = (itemId) => {
    setOpenSolutions((current) => ({ ...current, [itemId]: !current[itemId] }));
  };

  const gradeResponses = async () => {
    if (!canGrade) {
      setMessage(`Finish every question before grading. Still missing: ${formatMissingFields(missingItems)}${missingItems.length > 4 ? ' | ...' : ''}`);
      return;
    }
    setGradeState('grading');
    setMessage('Semantic grading in progress...');
    try {
      const nextGrades = await semanticGradeAllItems(config, responses);
      setGrades(nextGrades);
      await saveResponses(responses, 'manual', nextGrades);
      const usedAi = Object.values(nextGrades).some((grade) => grade?.mode === 'llm' || grade?.mode === 'semantic');
      setMessage(usedAi ? 'Semantic grading complete.' : 'Grading complete using the rule fallback. AI semantic grader was unavailable.');
    } finally {
      setGradeState('idle');
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.035) }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>{config.heading}</Typography>
          <Typography variant="body2" color="text.secondary">{config.description}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <Typography variant="caption" color="text.secondary">{completedCount}/{config.items.length} complete</Typography>
          {summary && <Chip label={`${summary.percent}%`} color={summary.percent >= 75 ? 'success' : summary.percent >= 50 ? 'warning' : 'default'} size="small" sx={{ fontWeight: 800 }} />}
          <Button variant="outlined" startIcon={<GradeIcon />} onClick={gradeResponses} disabled={saveState === 'saving' || loadState === 'loading' || gradeState === 'grading' || !canGrade} sx={{ textTransform: 'none', fontWeight: 800 }}>
            {gradeState === 'grading' ? 'Grading...' : 'Grade answers'}
          </Button>
          <Button variant="contained" startIcon={saveState === 'saving' ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />} onClick={() => saveResponses()} disabled={saveState === 'saving' || loadState === 'loading'} sx={{ textTransform: 'none', fontWeight: 800 }}>
            {saveState === 'saving' ? 'Saving...' : 'Save now'}
          </Button>
        </Stack>
      </Stack>

      {message && <Alert severity={saveState === 'error' ? 'error' : 'success'} sx={{ mt: 1.5 }}>{message}</Alert>}
      {missingItems.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Grade answers unlocks after all questions are attempted. Still missing: {formatMissingFields(missingItems)}{missingItems.length > 4 ? ' | ...' : ''}
        </Alert>
      )}
      {summary && (
        <Alert severity={summary.percent >= 70 ? 'success' : 'info'} sx={{ mt: 1.5 }}>
          Score: {summary.score}/{summary.max} ({summary.percent}%). {summary.weaknesses.length ? `Main development areas: ${summary.weaknesses.join(', ')}.` : 'No major weak lens detected.'}
        </Alert>
      )}

      {config.introAlert && <Alert severity="info" sx={{ mt: 1.5 }}>{config.introAlert}</Alert>}
      {config.decisionFramework && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`, bgcolor: alpha(theme.palette.warning.main, 0.06) }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>{config.decisionFramework.title || 'Decision framework'}</Typography>
          {config.decisionFramework.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {config.decisionFramework.description}
            </Typography>
          )}
          {config.decisionFramework.rules?.length > 0 && (
            <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.25 }}>
              {config.decisionFramework.rules.map((rule) => (
                <Typography key={rule} component="li" variant="body2">{rule}</Typography>
              ))}
            </Stack>
          )}
          {config.decisionFramework.responseFrame && (
            <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1, bgcolor: alpha(theme.palette.background.paper, 0.72) }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 900, textTransform: 'uppercase' }}>Response frame</Typography>
              <Typography variant="body2">{config.decisionFramework.responseFrame}</Typography>
            </Box>
          )}
        </Box>
      )}

      {loadState === 'loading' ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading your saved answers...</Typography>
        </Stack>
      ) : config.items.map((item) => (
        <Box key={item.id} sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: 'background.paper' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>{item.title}</Typography>
          {item.scenario && (
            <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
              <Typography variant="body2" color="text.secondary">{item.scenario}</Typography>
            </Box>
          )}
          {grades[item.id] && <Alert severity={grades[item.id].percent >= 70 ? 'success' : 'info'} sx={{ mb: 1.5 }}>{item.title} score: {grades[item.id].score}/{grades[item.id].max} ({grades[item.id].percent}%). {grades[item.id].feedback}</Alert>}
          {item.tables.map((table, index) => <DataTable key={`${item.id}-${index}`} table={table} />)}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Try the answer first, then open the worked guide if you need the calculation path or wording check.
            </Typography>
            <Button size="small" variant="outlined" onClick={() => toggleSolution(item.id)} sx={{ textTransform: 'none', fontWeight: 800, alignSelf: { xs: 'stretch', sm: 'center' } }}>
              {openSolutions[item.id] ? 'Hide worked solution' : 'Show worked solution'}
            </Button>
          </Stack>
          {openSolutions[item.id] && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`, bgcolor: alpha(theme.palette.success.main, 0.055) }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>Worked solution guide</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Use this to check the reasoning, then write your own answer in the fields below before saving or grading.
              </Typography>
              <Stack spacing={1.1}>
                {config.lenses.map(([key, label]) => (
                  <Box key={`${item.id}-solution-${key}`}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 900, textTransform: 'uppercase' }}>{label}</Typography>
                    <Typography variant="body2">{item.answerGuide?.[key] || 'No model answer has been added for this lens yet.'}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          {config.workingField && (
            <Box sx={{ mt: 2, p: 1.5, border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`, borderRadius: 1, bgcolor: alpha(theme.palette.info.main, 0.045) }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>{config.workingField.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {config.workingField.prompts?.[item.id] || 'Show your working before deciding the answer below.'}
              </Typography>
              <TextField
                value={responses[item.id]?.[config.workingField.key] || ''}
                onChange={(event) => updateAnswer(item.id, config.workingField.key, event.target.value)}
                placeholder={config.workingField.placeholder || ''}
                size="small"
                fullWidth
                multiline
                minRows={4}
              />
            </Box>
          )}
          <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <Box component="table" sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th': { bgcolor: alpha(theme.palette.primary.main, 0.09), fontWeight: 800 },
              '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 1.25, py: 0.9, textAlign: 'left', verticalAlign: 'top' },
              '& th:first-of-type, & td:first-of-type': { width: { xs: 150, sm: 210 } },
            }}>
              <thead><tr><th>Lens</th><th>Answer</th></tr></thead>
              <tbody>{config.lenses.map(([key, label]) => {
                const lensResult = grades[item.id]?.lensResults?.[key] || null;
                const missed = lensResult && lensResult.status !== 'correct';
                return (
                  <tr key={key}>
                    <td>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {lensResult?.status === 'correct' && <CheckCircleIcon color="success" fontSize="small" />}
                        {lensResult?.status === 'partial' && <ErrorOutlineIcon color="warning" fontSize="small" />}
                        {lensResult?.status === 'missed' && <ErrorOutlineIcon color="error" fontSize="small" />}
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                      </Stack>
                    </td>
                    <td>
                      <TextField value={responses[item.id]?.[key] || ''} onChange={(event) => updateAnswer(item.id, key, event.target.value)} placeholder={`Type your ${label.toLowerCase()}...`} size="small" fullWidth multiline minRows={key === 'learner_sentence' ? 2 : 1} color={lensResult?.status === 'correct' ? 'success' : lensResult?.status === 'partial' ? 'warning' : 'primary'} focused={Boolean(lensResult)} />
                      {missed && (
                        <Alert severity={lensResult.status === 'partial' ? 'warning' : 'error'} sx={{ mt: 0.75, py: 0 }}>
                          <Typography variant="caption" component="div">Expected direction: {lensResult.expected}</Typography>
                          {lensResult.reason && <Typography variant="caption" component="div">{lensResult.reason}</Typography>}
                        </Alert>
                      )}
                    </td>
                  </tr>
                );
              })}</tbody>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
